<template>
  <div>
    <BaseModal
      v-cloak
      :header-text="headerText"
      :modal-id="modalDomId"
      :is-open="modalOpen"
      :save-state="saveState"
      :remove-cancel-button="contentType === 'inline-edit'"
      :cancel-button-text="cancelButtonText"
      :confirm-button-text="confirmButtonText"
      :disable-translations="userField === 'locale'"
      @opened="handleModalOpened"
      @negative-response="negativeResponse"
      @positive-response="positiveResponse"
      @cancel="modalOpen = false"
    >
      <div class="p-t-15 p-b-15">
        <slot />
        <div v-if="contentType === 'input'">
          <input v-model="inputValueState" class="form-control tt-input" maxlength="50" />
        </div>
        <div v-if="userField === 'automatic-currency-conversion'">
          <p>
            {{
              $t(
                'Would you like to have rates in foreign markets converted to your local currency?',
              )
            }}
          </p>
        </div>
        <div v-if="userField === 'availability'">
          <EditAvailability
            ref="edit-availability"
            :is-me="true"
            :show-input-only="true"
            :initial-availability="autofillAvailabilitySelection"
            :initial-available-date="autofillAvailabilityDate"
            @saving="inlineEditComponentIsSaving"
            @done-saving="saveState = 'fresh'"
            @user-interacted="userHasInteractedWithContents = true"
          />
        </div>
      </div>
    </BaseModal>
  </div>
</template>

<script>
import moment from 'moment'
import { UPDATE_USER_STATE } from '@/state/mutation-types'
import { USER_ACTIONS } from '@/state/action-types'
import enums from '@/utils/enum'
import { UPDATE_FREELANCER, UPDATE_COMPANY } from '@/state/apollo/accounts'
import { NetworkError } from '@/utils/notification'
import BaseModal from '@/components/BaseModal.vue'
import EditAvailability from '@/components/EditAvailability.vue'

export default {
  components: {
    BaseModal,
    EditAvailability,
  },
  props: {
    inputValue: { type: String, default: '' },
    userField: { type: String, default: '' },
    customContentType: { type: String, default: '' },
    customModalDomId: { type: String, default: '' },
    customHeaderText: { type: String, default: '' },
    customConfirmButtonText: { type: String, default: '' },
    customCancelButtonText: { type: String, default: '' },
    openTrigger: { type: Boolean, default: false },
    autoSuggestAvailability: { type: String, default: '' },
  },
  data() {
    return {
      modalOpen: false,
      saveState: 'fresh',
      userHasInteractedWithContents: false,
      inputValueState: this.inputValue.slice(0),
    }
  },
  computed: {
    contentType() {
      switch (this.userField) {
        case 'availability':
          return 'inline-edit'
        case 'automatic-currency-conversion':
          return 'choice'
        default:
          return this.customContentType || 'input'
      }
    },
    modalDomId() {
      return this.customModalDomId || this.userField || 'settings-modal'
    },
    confirmButtonText() {
      if (this.customConfirmButtonText) {
        // If it's custom, assume translation was handled externally
        return this.customConfirmButtonText
      }
      switch (this.contentType) {
        case 'choice':
          return $t('Yes, please')
        case 'inline-edit':
          return this.userHasInteractedWithContents ? $t('done') : $t('Confirm availability')
        case 'confirm':
          return $t('Confirm')
        default:
          return $t('save')
      }
    },
    cancelButtonText() {
      if (this.customCancelButtonText) {
        // If it's custom, assume translation was handled externally
        return this.customCancelButtonText
      }
      return this.contentType === 'choice' ? $t('No thanks') : $t('Cancel')
    },
    headerText() {
      if (this.customHeaderText) {
        // If it's custom, assume translation was handled externally
        return this.customHeaderText
      }
      switch (this.userField) {
        case 'availability':
          return $t("It's time to confirm your availability")
        case 'automatic-currency-conversion':
          return $t('Convert foreign currency?')
        default:
          return 'Please answer this question'
      }
    },
    autofillAvailabilitySelection() {
      return this.autoSuggestAvailability ? 'ondate' : this.account.job_ready
    },
    autofillAvailabilityDate() {
      return this.autoSuggestAvailability
        ? this.autoSuggestAvailability
        : this.account.job_ready_date
    },
  },
  watch: {
    modalOpen(newModalOpen) {
      if (!newModalOpen) {
        this.$emit('closed')
      }
    },
    openTrigger(newOpenProp) {
      if (newOpenProp) {
        this.triggerTheModal()
      }
    },
  },
  mounted() {
    if (this.openTrigger) {
      this.triggerTheModal()
    }
  },
  methods: {
    handleModalOpened() {
      this.saveState = 'fresh'
      if (this.userField === 'availability') {
        this.updateLastSeenAvailabilityPrompt()
      }
    },
    positiveResponse() {
      if (this.userField === 'locale') {
        this.switchToEnglish()
        return
      }
      if (this.userField === 'availability') {
        this.$refs['edit-availability'].saveChanges()
        this.touchAvailability()
        return
      }
      if (this.userField === 'automatic-currency-conversion') {
        this.setCurrencyConversion(true)
        return
      }
      if (this.userField === 'team_name') {
        this.updateTeamName()
        return
      }

      switch (this.contentType) {
        case 'input':
          this.saveAndDismiss(this.userField, this.inputValueState, true)
          break
        case 'confirm':
          this.saveAndDismiss(this.userField, true)
          break
      }
    },

    negativeResponse() {
      if (this.userField === 'locale') {
        this.switchToDanish()
        return
      }
      if (this.userField === 'automatic-currency-conversion') {
        this.setCurrencyConversion(false)
      } else {
        this.modalOpen = false
      }
    },
    inlineEditComponentIsSaving() {
      this.saveState = 'saving'
    },
    async saveSetting(fieldName, fieldValue) {
      this.saveState = 'saving'
      try {
        await this.$store.dispatch(USER_ACTIONS.UPDATE_USER_FIELD, {
          [fieldName]: fieldValue,
        })
        return true
      } catch (e) {
        return false
      }
    },
    async saveAndDismiss(fieldName, fieldValue, shouldRefresh) {
      if (await this.saveSetting(fieldName, fieldValue)) {
        this.modalHasSavedActions()
        if (shouldRefresh) {
          window.location.reload()
        }
      } else {
        this.modalHasSaveFailedActions()
      }
    },
    modalHasSavedActions() {
      this.saveState = 'saved'
      setTimeout(() => {
        this.modalOpen = false
      }, 1000)
    },
    modalHasSaveFailedActions() {
      this.saveState = 'failed'
      this.$store.commit('sendNotification', {
        type: enums.notification.types.ERROR,
        sender: this,
        severity: enums.notification.severity.CRITICAL,
      })
      setTimeout(() => {
        this.saveState = 'fresh'
      }, 3000)
    },
    async setCurrencyConversion(enable) {
      this.saveState = 'saving'
      try {
        await this.$store.dispatch(USER_ACTIONS.UPDATE_USER_FIELD, {
          settings: { automatic_currency_conversion: enable },
        })
        await this.$store.dispatch(USER_ACTIONS.UPDATE_USER_FIELD, {
          meta: { has_seen_foreign_currency: true },
        })
        this.$store.commit(UPDATE_USER_STATE, {
          fieldName: 'automatic_currency_conversion',
          fieldValue: enable,
        })
        this.$store.commit(UPDATE_USER_STATE, {
          fieldName: 'has_seen_foreign_currency',
          fieldValue: true,
        })
        this.modalHasSavedActions()
      } catch (e) {
        this.modalHasSaveFailedActions()
      }
    },
    async switchToEnglish() {
      if (await this.saveSetting('locale', 'en')) {
        this.saveState = 'saved'
        location.reload()
      } else {
        this.modalHasSaveFailedActions()
      }
    },
    async switchToDanish() {
      this.saveState = 'saving'
      try {
        await this.$store.dispatch(USER_ACTIONS.UPDATE_USER_FIELD, {
          meta: { do_not_prompt_for_language: true },
        })
        this.modalOpen = false
      } catch (e) {
        this.modalHasSaveFailedActions()
      }
    },
    async updateTeamName() {
      this.saveState = 'saving'
      try {
        await this.$apollo.mutate({
          mutation: UPDATE_COMPANY,
          variables: {
            input: {
              globalId: this.account.globalId,
              team_name: this.inputValueState,
            },
          },
        })
        this.modalOpen = false
        location.reload() // Need to reload page for the name change to be displayed
      } catch (e) {
        this.modalHasSaveFailedActions()
      }
    },
    async touchAvailability() {
      this.saveState = 'saving'
      try {
        await this.$apollo.mutate({
          mutation: UPDATE_FREELANCER,
          variables: {
            input: {
              globalId: this.account.globalId,
              meta: {
                last_updated_availability: moment().format('YYYY-MM-DD HH:mm:ss'),
              },
            },
          },
        })

        this.modalOpen = false
        // eslint-disable-next-line no-empty
      } catch (e) {}
    },
    async updateLastSeenAvailabilityPrompt() {
      try {
        await this.$apollo.mutate({
          mutation: UPDATE_FREELANCER,
          variables: {
            input: {
              globalId: this.account.globalId,
              meta: {
                last_seen_availability_prompt: moment().format('YYYY-MM-DD HH:mm:ss'),
              },
            },
          },
        })
      } catch (e) {
        new NetworkError({
          severity: enums.notification.severity.CRITICAL,
          ex: e,
        }).dispatch(this)
      }
    },
    triggerTheModal() {
      this.modalOpen = true
    },
  },
}
</script>

<style scoped lang="scss"></style>
